/**
 * @file   src/components/Common/PriceHistogramSlider.js
 * @brief  Airbnb-style fixed-scale price histogram above a dual-handle range slider
 * @date   February, 2026
 * @copyright (c) 2026, Gitcha
 */
// [US-002] Fixed-scale histogram - bins span [0, max] so bars never shift while sliding
import React, { useMemo, useEffect, useRef, useCallback } from "react";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";
import { calculateSmartCap } from "../../helpers/priceCapUtils";

const NUM_BINS = 30;
const MIN_PRICES_TO_LOCK = 5;

/**
 * Extract numeric prices from want ad objects.
 * Filters out no-min/no-max entries - only uses actual budget values.
 */
const extractPrices = (priceData) => {
  if (!priceData || !Array.isArray(priceData)) return [];

  return priceData
    .map((item) => {
      if (typeof item === "number" && item > 0) return item;
      if (typeof item === "object" && item !== null) {
        const mn = Number(item.Budget_mn) || 0;
        const mx = Number(item.Budget_mx) || 0;
        if (mn > 0 && mx > 0) return (mn + mx) / 2;
        if (mn > 0) return mn;
        if (mx > 0) return mx;
      }
      return null;
    })
    .filter((v) => v !== null && v > 0);
};

/**
 * Compute fixed-scale histogram bins spanning [0, max].
 * Each bin width = max / NUM_BINS. Prices above max go in last bin.
 * The histogram shape is fixed - only bar colors change when sliding.
 */
const computeFixedBins = (prices, max) => {
  if (!prices || prices.length < 5 || !max || max <= 0) {
    return null;
  }

  const binWidth = max / NUM_BINS;
  const bins = Array.from({ length: NUM_BINS }, () => ({ count: 0 }));

  prices.forEach((price) => {
    let binIndex = Math.floor(price / binWidth);
    // Prices at or above max go in last bin
    if (binIndex >= NUM_BINS) binIndex = NUM_BINS - 1;
    if (binIndex < 0) binIndex = 0;
    bins[binIndex].count++;
  });

  return bins;
};

const PriceHistogramSlider = (props) => {
  const {
    min,
    max,
    value,
    onInput,
    defaultValue,
    sliderClass,
    stepValue = 0,
    rangeSlideDisabled,
    thumbsDisabled,
    priceData = [],
    adType,
    onCapCalculated,
    resetKey,
  } = props;

  const prices = useMemo(() => extractPrices(priceData), [priceData]);

  // Lock prices after first meaningful batch so filtered API results
  // don't shift the histogram or recalculate the cap mid-interaction
  const lockedPricesRef = useRef(null);
  const lockedAdTypeRef = useRef(adType);
  const lockedResetKeyRef = useRef(resetKey);

  // Reset lock when adType or resetKey changes
  if (adType !== lockedAdTypeRef.current || resetKey !== lockedResetKeyRef.current) {
    lockedPricesRef.current = null;
    lockedAdTypeRef.current = adType;
    lockedResetKeyRef.current = resetKey;
  }

  // Lock once we have enough data
  if (!lockedPricesRef.current && prices.length >= MIN_PRICES_TO_LOCK) {
    lockedPricesRef.current = prices;
  }

  const stablePrices = lockedPricesRef.current || prices;

  // Track drag state - suppress onCapCalculated during active drag
  const isDraggingRef = useRef(false);

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const handleDragEnd = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // Calculate smart cap from stable (locked) prices
  const capInfo = useMemo(
    () => calculateSmartCap(stablePrices, adType),
    [stablePrices, adType]
  );

  const prevCapRef = useRef(null);
  useEffect(() => {
    if (onCapCalculated && capInfo && !isDraggingRef.current) {
      const capKey = `${capInfo.cap}-${capInfo.step}-${capInfo.fallback}`;
      if (prevCapRef.current !== capKey) {
        prevCapRef.current = capKey;
        onCapCalculated(capInfo);
      }
    }
  }, [capInfo, onCapCalculated]);

  // Fixed bins from stable (locked) prices spanning [0, max]
  const bins = useMemo(() => computeFixedBins(stablePrices, max), [stablePrices, max]);

  const sliderMin = value ? value[0] : min;
  const sliderMax = value ? value[1] : max;

  const hasBins = bins && bins.length > 0;
  const maxCount = hasBins ? Math.max(...bins.map((b) => b.count), 1) : 1;

  return (
    <div className="price-histogram-slider">
      <div className="price-histogram" aria-hidden="true">
        <div className="price-histogram__bars">
          {hasBins && bins.map((bin, i) => {
            const heightPct = (bin.count / maxCount) * 100;
            // Bars span [0, max] same as slider - positions always match
            const barStart = (i / NUM_BINS) * max;
            const barEnd = ((i + 1) / NUM_BINS) * max;
            const isActive = barEnd > sliderMin && barStart < sliderMax;
            const isEmpty = bin.count === 0;

            return (
              <div
                key={i}
                className={`price-histogram__bar${isActive ? " price-histogram__bar--active" : ""}${isEmpty ? " price-histogram__bar--empty" : ""}`}
                style={{
                  height: `${isEmpty ? 2 : Math.max(heightPct, 4)}%`,
                }}
              />
            );
          })}
        </div>
      </div>
      <RangeSlider
        className={sliderClass}
        min={min}
        max={max}
        value={value}
        onInput={onInput}
        step={stepValue}
        defaultValue={defaultValue}
        onThumbDragStart={handleDragStart}
        onThumbDragEnd={handleDragEnd}
        onRangeDragStart={handleDragStart}
        onRangeDragEnd={handleDragEnd}
        thumbsDisabled={thumbsDisabled}
        rangeSlideDisabled={rangeSlideDisabled}
      />
    </div>
  );
};

export default PriceHistogramSlider;
